import React, { useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { fetchHelpCenterAction } from '@helpcenter/app/epics/model/help-center';
import { ScreenName } from '@helpcenter/constants';
import { withPathParams } from '@helpcenter/with-path-params';
import type { InjectedProps as WithParamsInjectedProps } from '@helpcenter/with-path-params';
import qs from 'query-string';
import { di } from 'react-magnetic-di';
import { usePathParam, useRouter, useRouterActions } from 'react-resource-router';
import type { RouteContext } from 'react-resource-router';
import type { State } from 'state';
import { fetchPortalAction } from 'state/actions/portal';
import type { UpdateSearchAction, ShowSearchAction } from 'state/actions/search';
import { updateSearch as updateSearchAction } from 'state/actions/search';
import { getProjectId } from 'state/selectors/portal';
import { getTerm } from 'state/selectors/search';
import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
import type { SelectValues } from '@atlassian/paginated-picker';
import CategoryFilter from '../category-filter';
import {
    useQueryParams,
    isSinglePortalSearch,
    isValidSearchTerm,
    getPortalIdAsNumber,
    SEARCH_BAR_HEIGHT,
} from '../common';
import { SearchBar } from '../search-bar';
import { SearchErrorBoundary } from '../search-error-boundary';
import { SearchResult } from '../search-result';
import ServiceDeskFilter from '../servicedesk-filter';
export interface Props {
    projectId: number;
    onSearch: (term: string) => UpdateSearchAction;
    fetchPortals: typeof fetchPortalAction;
    fetchHelpCenter: typeof fetchHelpCenterAction;
}

type SearchProps = Props & WithAnalyticsEventsProps & WithParamsInjectedProps;

export const AllResults = ({ projectId, onSearch, fetchHelpCenter, fetchPortals }: SearchProps) => {
    di(useQueryParams);
    const { goBack } = useRouterActions();
    const [{ location }, { replace }] = useRouter();
    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const queryStringParams = useQueryParams();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { q: searchTermValue = '' }: { q: string } = queryStringParams;
    const [portalId] = usePathParam('portalId');
    const portalIdAsNumber = getPortalIdAsNumber(portalId);
    const showSearchResults = isValidSearchTerm(searchTermValue);
    const showCategoryFilter = isSinglePortalSearch(portalIdAsNumber);
    // On single portal search, we fetch the needed portals data for the filters
    useEffect(() => {
        if (isSinglePortalSearch(portalIdAsNumber)) {
            fetchPortals({ id: portalIdAsNumber, expand: ['kbs', 'reqTypes', 'reqGroups', 'orderMapping'] });
        }
    }, [fetchHelpCenter, fetchPortals, portalIdAsNumber]);

    const onServiceDeskFilterChange = useCallback(
        (options: SelectValues) => {
            const selectedPortalIds = options.map((option) => option.value);
            const queryString = qs.stringify({
                ...queryStringParams,
                portalIds: selectedPortalIds,
            });

            replace(`${location.pathname}?${queryString}`) as unknown as void;
        },
        [location.pathname, queryStringParams, replace]
    );

    const onCategoryFilterChange = useCallback(
        (options: SelectValues) => {
            const selectedCategoryIds = options.map((option) => option.value);
            const queryString = qs.stringify({
                ...queryStringParams,
                categoryIds: selectedCategoryIds,
            });

            replace(`${location.pathname}?${queryString}`) as unknown as void;
        },
        [location.pathname, queryStringParams, replace]
    );

    return (
        <SearchErrorBoundary id="search-all-results">
            <Box
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                xcss={xcss({
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                    height: `calc(100% - ${SEARCH_BAR_HEIGHT})`,
                })}
            >
                <SearchBar
                    term={searchTermValue}
                    onChange={onSearch}
                    onClose={goBack as unknown as () => ShowSearchAction}
                />
                {showSearchResults && (
                    <Box
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                        xcss={xcss({
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            position: 'relative',
                            height: '100%',
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                            top: SEARCH_BAR_HEIGHT,
                            backgroundColor: 'elevation.surface.overlay',
                            paddingTop: 'space.300',
                            paddingBottom: 'space.200',
                            overflow: 'auto',
                        })}
                    >
                        <Box
                            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Disabled to rollout go/ui-styling-standard tooling, please resolve
                            xcss={xcss({
                                width: '90%',
                                pointerEvents: 'auto',
                                [media.above.sm]: {
                                    width: '50%',
                                },
                            })}
                        >
                            <Inline>
                                {showCategoryFilter ? (
                                    <CategoryFilter
                                        onChange={onCategoryFilterChange}
                                        portalId={portalIdAsNumber}
                                        projectId={projectId}
                                        categoryIds={[]}
                                    />
                                ) : (
                                    <ServiceDeskFilter onChange={onServiceDeskFilterChange} portalIds={[]} />
                                )}
                            </Inline>
                            <SearchResult term={searchTermValue} />
                        </Box>
                    </Box>
                )}
            </Box>
        </SearchErrorBoundary>
    );
};

type ExternalProps = Omit<RouteContext, 'action'>;

const selector = createSelector(
    getTerm,
    (state: State, props: ExternalProps) => getProjectId(state, getPortalIdAsNumber(props.match.params.portalId)),
    (term, projectId) => ({
        term,
        projectId,
    })
);

const ConnectedAllResults = connect(selector, {
    onSearch: (term: string) => updateSearchAction(term, {}),
    fetchPortals: fetchPortalAction,
    fetchHelpCenter: fetchHelpCenterAction,
})(
    withPathParams(
        withAnalyticsContext({ component: ScreenName.HELP_CENTER_SEARCH })(withAnalyticsEvents()(AllResults))
    )
);

export default ConnectedAllResults;
